import Image from 'next/image';
import Container from 'components/container/container';
import ProductPage from 'components/product';
import ProductFaqSection from 'components/product/product-faq';
import {
  ProductDivider,
  ProductFeatureCollapsible,
  ProductFeatureContainer,
  ProductFeatureLayoutGrid,
  ProductFeatureLayoutGridNode,
  ProductFeatureRequest,
  ProductFeatureShowMoreContainer,
  ProductFeatureShowMoreLink,
  ProductMcpHighlight,
  ProductPlan,
  ProductQuote,
} from 'components/product/product.styles';
import Link from 'next/link';
import { useState } from 'react';
import Plan from 'shared/components/plan';

import { CUBUILD_URL, LIBUM_API_NEXUM, LIBUM_FEEDBACK, LIBUM_URL, RAY_LINKEDIN } from 'shared/constants/globals';
import { PRODUCTS } from 'shared/data/products';
import { useTheme } from 'shared/styles/theme';

import HOVER_ARROW from 'public/icons/hover_arrow.svg';
import SPARKLE from 'public/icons/sparkle.svg';

const NEXUM_API = PRODUCTS.find((p) => p.id === 'nexum-api')!;

const NEXUM_FAQ = [
  {
    question: 'Does NexumAPI support every SymXchange service?',
    answer:
      'Yes. NexumAPI maps one-to-one to every SymXchange SOAP service and operation, and stays in version parity as your Symitar core updates — so you can reach anything SymXchange exposes with modern JSON instead of SOAP.',
  },
  {
    question: 'Can I self-host NexumAPI, or do you host it?',
    answer:
      'Either. Run NexumAPI yourself, or use NaaS (NexumAPI as a Service) — fully managed by Libum with an optional VPN tunnel, load balancing, automatic updates, and high availability.',
  },
  {
    question: 'Can you use AI with the Symitar core?',
    answer:
      'Yes. The NexumAPI MCP server exposes the Symitar core through SymXchange to AI assistants over the Model Context Protocol, so your team can ask in plain language instead of hand-writing requests.',
  },
  {
    question: 'Which AI tools work with the Symitar core?',
    answer:
      'Any MCP-compatible client — including Claude, ChatGPT, Cursor, and Codex — can connect to the NexumAPI MCP server and work with your Symitar core.',
  },
  {
    question: 'Is it safe to connect AI to a core banking system?',
    answer:
      'Yes. The NexumAPI MCP server is read-only by default, keeps your SymXchange credentials separate from the AI, supports policy allowlists for approved tools, operations, credential types, and identities, requires confirmation for high-impact writes, and logs every call for a complete audit trail.',
  },
  {
    question: 'Can we control what the NexumAPI MCP server can access?',
    answer:
      'Yes. MCP inherits your NexumAPI service and version gating, then can be narrowed further with policy allowlists for specific MCP tools, SymXchange service verbs, operations, credential types, and authenticated user identities.',
  },
  {
    question: 'Do you need to write code to use NexumAPI?',
    answer:
      'For the REST API you send JSON instead of SOAP, with no XML to wrangle. And with the NexumAPI MCP server you can skip code entirely — just ask your AI assistant in natural language.',
  },
];

export default function NexumAPI() {
  const { theme } = useTheme();
  const title = 'NexumAPI | Libum';
  const description = `NexumAPI is the fastest way to build on SymXchange — modern JSON / REST, plus natural-language AI access through the NexumAPI MCP server.`;
  const keywords =
    'NexumAPI,NexumAPI MCP,Libum API,SymXchange,SymXchange AI,Symitar AI,Model Context Protocol,MCP,AI SymXchange,JSON SymXchange,SymXchange SOAP alternative,SymXchange API,Jack Henry API,PowerOn API,Symitar,Symitar software,Jack Henry software,credit union software';
  const url = `${LIBUM_URL}/nexum`;

  const [showMore, setShowMore] = useState(false);

  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  const productSchemaData = {
    name: 'NexumAPI',
    description: description,
    category: 'DeveloperApplication',
    operatingSystem: 'Windows, macOS, Linux',
    image: `${LIBUM_URL}/images/9e7d6c5b-4a3f-2e1d-8c7b-6a5f4e3d2c1b.png`,
    keywords:
      'Symitar AI, AI for Symitar, SymXchange AI, NexumAPI MCP, Model Context Protocol, natural language SymXchange, Jack Henry AI',
    featureList: [
      'Natural-language AI access to SymXchange through the NexumAPI MCP server',
      'Connect Claude, ChatGPT, Cursor, Codex, or any MCP-compatible client to the Symitar core',
      'Read-only by default with granular policy allowlists, write confirmation, and full audit logging',
      'Modern JSON / REST interface to every SymXchange service and operation',
    ],
  };

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="nexum"
      product={productSchemaData}
      breadcrumbs={[
        { name: 'Products', url: `${LIBUM_URL}/products` },
        { name: 'NexumAPI', url },
      ]}
      faq={NEXUM_FAQ}
    >
      <div style={{ width: '100%' }}>
        <ProductPage product={NEXUM_API}>
          <h1>NexumAPI</h1>
          <h2>The fastest way to develop with SymXchange.</h2>
          <ProductDivider />
          <p id="overview">
            With NexumAPI, your systems can freely communicate with the Symitar core platform over SymXchange using our
            modern JSON / RESTful interface to rapidly engineer the next best credit union app using standard JSON data
            representation by default.
          </p>
          <p>
            Originally unveiled at{' '}
            <Link href={CUBUILD_URL} target="_blank">
              CU Build
            </Link>{' '}
            in 2023 — NexumAPI is a translation engine that translates SymXchange request/response payloads in the
            single millisecond range and that&apos;s for every service, every operation, on any supported version.
          </p>
          <p>
            Oh, and we have interactive{' '}
            <Link href={LIBUM_API_NEXUM} target="_blank">
              online docs
            </Link>{' '}
            for the entire SymXchange API.
          </p>
          <figure style={{ margin: '32px 0', padding: '0 80px' }}>
            <div style={{ position: 'relative', width: '100%', marginBottom: '32px' }}>
              <Image
                src={
                  theme === 'dark'
                    ? '/images/a1b2c3d4-5e6f-7a8b-9c0d-1e2f3a4b5c6d.png'
                    : '/images/f4e3d2c1-b0a9-8f7e-6d5c-4b3a2f1e0d9c.png'
                }
                alt="SymXchange service interface"
                width={580}
                height={357}
                style={{ width: '70%', height: 'auto', borderRadius: '8px', position: 'relative', zIndex: 1 }}
              />
              <Image
                src={
                  theme === 'dark'
                    ? '/images/9e7d6c5b-4a3f-2e1d-8c7b-6a5f4e3d2c1b.png'
                    : '/images/d5e6f7a8-2b3c-4d5e-6f7a-8b9c0d1e2f3a.png'
                }
                alt="NexumAPI documentation"
                width={580}
                height={357}
                style={{
                  position: 'absolute',
                  bottom: '-16px',
                  right: '-16px',
                  width: '55%',
                  height: 'auto',
                  borderRadius: '8px',
                  zIndex: 2,
                  boxShadow: '0 8px 15px rgba(0, 0, 0, 0.3)',
                }}
              />
            </div>
            <figcaption style={{ marginTop: '12px', textAlign: 'center', fontSize: '14px', opacity: 0.8 }}>
              API payload before with SymX (top left) and after with NexumAPI (bottom right)
            </figcaption>
          </figure>
          <ProductDivider />
          <ProductMcpHighlight id="mcp">
            <div className="eyebrow">
              <SPARKLE />
              <span className="label">New</span>
            </div>
            <h3>Now your Symitar core speaks AI</h3>
            <div className="lede">
              Connect Claude, ChatGPT, Cursor, Codex, or any MCP-compatible client straight to SymXchange and ask for
              what you need in plain language. Read-only out of the box — with writes off by default, granular policy
              controls, confirmation for high-impact writes, and a full audit trail — so it&apos;s safe enough for a
              banking core.
            </div>
            <div className="prompts">
              <span className="prompt">&ldquo;Look up the member tied to this debit card.&rdquo;</span>
              <span className="prompt">&ldquo;What would a 72-month, $40k RV loan run per month?&rdquo;</span>
              <span className="prompt">&ldquo;Verify check #1183 on this account.&rdquo;</span>
            </div>
            <Link href="/blog/symitar-core-speaks-ai">
              Read about NexumAPI MCP
              <HOVER_ARROW />
            </Link>
          </ProductMcpHighlight>
          <ProductDivider />
          <ProductFeatureContainer id="features">
            <ProductFeatureLayoutGrid className="featured">
              {NEXUM_API.features
                .filter((f) => f.isFeatured)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={`isFeatured ${feature.id === 'mcp' ? 'full-row' : ''} ${
                      feature.docsLink ? 'has-link' : ''
                    }`}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="image">
                      {feature.id === 'mcp' ? (
                        // Silent, decorative demo loop (no audio track) of the NexumAPI MCP in use.
                        <video className="mcp-video" autoPlay muted loop playsInline preload="metadata">
                          <source src="/videos/naas-mcp.webm" type="video/webm" />
                          <source src="/videos/naas-mcp.mp4" type="video/mp4" />
                        </video>
                      ) : (
                        <Image
                          src={
                            feature.id === 'naas'
                              ? theme === 'dark'
                                ? '/images/9e7d6c5b-4a3f-2e1d-8c7b-6a5f4e3d2c1b.png'
                                : '/images/d5e6f7a8-2b3c-4d5e-6f7a-8b9c0d1e2f3a.png'
                              : feature.imagePath
                          }
                          alt={`${feature.name} feature screenshot`}
                          width={400}
                          height={250}
                          style={{ maxHeight: '100%', width: 'auto', height: 'auto' }}
                        />
                      )}
                    </div>
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid>
              {NEXUM_API.features
                .filter((f) => !f.isFeatured && !f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureCollapsible className={showMore ? 'opened' : ''}>
              <div>
                <ProductFeatureLayoutGrid>
                  {NEXUM_API.features
                    .filter((f) => f.isHidden)
                    .map((feature) => (
                      <ProductFeatureLayoutGridNode
                        key={feature.id}
                        className={feature.docsLink ? 'has-link' : ''}
                        onClick={() => {
                          feature.docsLink && window.open(feature.docsLink, '_blank');
                        }}
                        id={feature.id}
                      >
                        <div className="content">
                          <div className="name">{feature.name}</div>
                          <div className="description">{feature.description}</div>
                        </div>
                      </ProductFeatureLayoutGridNode>
                    ))}
                </ProductFeatureLayoutGrid>
              </div>
            </ProductFeatureCollapsible>
          </ProductFeatureContainer>
          <ProductFeatureShowMoreContainer>
            <ProductFeatureShowMoreLink onClick={handleShowMoreClick}>
              {showMore ? 'Show less' : 'Show more'}
            </ProductFeatureShowMoreLink>
          </ProductFeatureShowMoreContainer>
          <ProductDivider />
          <ProductQuote>
            &quot;NexumAPI isn&apos;t just another middleware or endpoint; it&apos;s a revolution for Jack Henry credit
            unions. It transforms the way we interact with the Symitar core, replacing outdated software and systems
            with a modern, streamlined approach. It significantly lowers the barrier to entry for working with the
            Symitar core via SymXchange.&quot;
            <br />
            <br />{' '}
            <i>
              <Link href={RAY_LINKEDIN} target="_blank">
                Ray Satterfield, Programmer II @ Dover Federal Credit Union
              </Link>
            </i>
          </ProductQuote>
          <ProductDivider />
          <ProductPlan id="offerings">
            <Plan
              header={NEXUM_API.plan.header}
              features={NEXUM_API.plan.features}
              offerings={NEXUM_API.plan.offerings}
            />
          </ProductPlan>
          <ProductDivider />
          <ProductFaqSection faq={NEXUM_FAQ} />
          <ProductDivider />
          <ProductFeatureRequest>
            <h3>Have a feature you&apos;d like to see in NexumAPI?</h3>
            <Link href={LIBUM_FEEDBACK}>Submit Your Idea</Link>
          </ProductFeatureRequest>
        </ProductPage>
      </div>
    </Container>
  );
}
